import { useMemo } from "react";
import { useQuery } from "react-query";
import { groupBy, isEmpty } from "lodash";

import { translateStatusSends } from "../../../utils/format";
import { getGraphsSMS } from "../../../services/metricsSms";

import { Header } from "../Header";
import { CardGraph } from "../CardGraph";
import { CardDashboard } from "../CardDashboard";

import { Box, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { useStyles } from "../styles";

export function SMS() {
  const styles = useStyles();

  const responseGraphsSMS = useQuery("responseGraphsSMS", getGraphsSMS);

  const sendNumbers = useMemo(() => {
    return responseGraphsSMS.data
      ?.map((_resp) => _resp.total)
      .reduce((previous, current) => previous + current, 0);
  }, [responseGraphsSMS]);

  const clicksNumber = useMemo(() => {
    return responseGraphsSMS.data
      ?.filter((_resp) => _resp.status === "MENSAGEM CLICADA")
      .map((_resp) => _resp.total)
      .reduce((previous, current) => previous + current, 0);
  }, [responseGraphsSMS]);

  const receivedNumber = useMemo(() => {
    return responseGraphsSMS.data
      ?.filter(
        (_resp) =>
          _resp.status === "ENVIADO COM CONFIRMACAO" ||
          _resp.status === "CONFIRMADO"
      )
      .map((_resp) => _resp.total)
      .reduce((previous, current) => previous + current, 0);
  }, [responseGraphsSMS]);

  const readNumber = useMemo(() => {
    return responseGraphsSMS.data
      ?.filter(
        (_resp) =>
          _resp.status === "MENSAGEM ABERTA" ||
          _resp.status === "MENSAGEM ENTREGUE"
      )
      .map((_resp) => _resp.total)
      .reduce((previous, current) => previous + current, 0);
  }, [responseGraphsSMS]);

  let response = useMemo(() => {
    return responseGraphsSMS.data?.map((_cont) => ({
      label: _cont.date,
      value: _cont.total,
      status: translateStatusSends(_cont.status),
    }));
  }, [responseGraphsSMS]);

  const graphsSMS: any = useMemo(() => {
    if (!response || response.length === 0) {
      return {
        wait: [],
        sent: [],
        received: [],
        canceled: [],
        error: [],
        read: [],
      };
    }
    const groupData = groupBy(response, ({ status }) => status);

    Object.entries(groupData).forEach((res): any => {
      let values = groupBy(res[1], ({ label }) => label);
      let result = Object.entries(values).map((value) => ({
        label: value[0],
        value: value[1]
          .map(({ value }) => value)
          .reduce((previous, current) => previous + current, 0),
      }));

      if (!isEmpty(result)) {
        return Object.assign(groupData, {
          [res[0]]: result.concat(groupData?.[res[0]] || []),
        });
      }
    });

    return { ...graphsSMS, ...groupData };
  }, [response]);

  const numbers = useMemo(() => {
    return {
      total: sendNumbers,
      clicks: clicksNumber,
      read: readNumber,
      received: receivedNumber,
    };
  }, [sendNumbers, clicksNumber, readNumber, receivedNumber]);

  const { wait, sent, received, read, error } = graphsSMS;

  const dataWait = useMemo(() => {
    return wait.map((value: any) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [wait]);

  const dataSent = useMemo(() => {
    return sent.map((value: any) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [sent]);

  const dataReceived = useMemo(() => {
    return received.map((value: any) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [received]);

  const dataRead = useMemo(() => {
    return read.map((value: any) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [read]);

  const dataError = useMemo(() => {
    return error.map((value: any) => ({
      x: value.label.toUpperCase(),
      y: value.value,
    }));
  }, [error]);

  if (responseGraphsSMS.isLoading) {
    return (
      <Stack
        spacing={2}
        justifyContent="center"
        alignItems="center"
        width="100%"
        height="62vh"
      >
        <Typography variant="subtitle1">Buscando informações</Typography>
        <LinearProgress variant="indeterminate" sx={{ width: "50%" }} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2} overflow="overlay" height="62vh">
      <Box className={styles.root}>
        <Header title="SMS" />
        <Stack>
          <Grid
            container
            display="flex"
            p="24px 24px 0 0"
            spacing={2}
            flexWrap="wrap"
          >
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={numbers.received}
                title={"Enviado com confirmação"}
                text={"A quantidade de sms enviados com confirmação."}
              />
            </Grid>
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={numbers.total}
                title={"Total de mensagens enviadas"}
                text={"A soma de sms que você enviou em todas as publicações."}
              />
            </Grid>
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={numbers.clicks}
                title={"Enviado sem confirmação"}
                text={"A quantidade de contatos que não visualizaram seus sms."}
              />
            </Grid>
            <Grid item md={5.5} lg={3} width="100%">
              <CardDashboard
                insights={numbers.read}
                title={"Mensagens que foram lidas"}
                text={"A quantidade de contatos que vizualizaram seus sms."}
              />
            </Grid>
          </Grid>
          <Stack flexDirection="column" p="24px 0" gap={3}>
            <Header />
            <Grid
              container
              display="flex"
              p="24px 24px 0 0"
              spacing={4}
              flexWrap="wrap"
            >
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Enviadas"
                  text="Quantas mensagens foram enviadas"
                  data_type="SENT"
                  data={[{
                    name: 'Enviadas',
                    data: dataSent
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Recebidos"
                  text="Quantos números receberam as mensagens"
                  data_type="RECEIVED"
                  data={[{
                    name: 'Recebidos',
                    data: dataReceived
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Lidos"
                  text="Quantos números leram as mensagens"
                  data_type="READ"
                  data={[{
                    name: 'Lidos',
                    data: dataRead
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Em espera"
                  text="Quantas mensagens faltam processar"
                  data_type="WAIT"
                  data={[{
                    name: 'Em espera',
                    data: dataWait
                  }]}
                />
              </Grid>
              <Grid item md={12} lg={6} width="100%">
                <CardGraph
                  title="Erros"
                  text="Quantas mensagens deram erro"
                  data_type="ERROR"
                  data={[{
                    name: 'Erros',
                    data: dataError
                  }]}
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
}
